import { useMemo, useState } from "react";

import { GenericTooltip } from "@components/tooltip-container";
import { useModelMetadata } from "@pages/mmm/hooks/use-model-metadata";

import { type ModelMetadataKpiFormula } from "../../explore-budgets/client-setting/types";
import { KpiSelect } from "../../shared/kpi-select";
import { ExportChannelInsightsModal } from "../export-channel-insights-modal";
import { useModelMetadataKpis } from "../hooks/use-model-kpis";
import { type ChannelInsightsColumn } from "../model-insights-content";
import { formatKpiEquation } from "../utils";

interface Props {
  selectedKpi: ModelMetadataKpiFormula;
  onChange: (kpi: ModelMetadataKpiFormula) => void;
  columns: ChannelInsightsColumn[];
  costCurvesRawData: any;
}

export const ModelFilters = ({
  selectedKpi,
  onChange,
  columns,
  costCurvesRawData,
}: Props) => {
  const [modelMetadata] = useModelMetadata();
  const { kpis } = useModelMetadataKpis();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const astTokens = useMemo(
    () =>
      formatKpiEquation(
        selectedKpi.equation,
        modelMetadata!.target_data_columns,
      ),
    [modelMetadata, selectedKpi],
  );

  return (
    <div className="flex items-start justify-between">
      <div className="flex gap-4 mr-4">
        <div className="flex flex-col">
          <label className="block text-sm mb-1 h-4 max-h-[16px]">
            <GenericTooltip content="Changing KPI impacts all charts on the page">
              KPI
            </GenericTooltip>
          </label>
          <div className="flex items-center gap-5">
            <KpiSelect
              className="w-72"
              value={selectedKpi.kpi_name}
              options={kpis.map((e) => ({
                label: e.friendly_name,
                value: e.kpi_name,
              }))}
              onChange={(value) =>
                onChange(kpis.find((e) => e.kpi_name === value)!)
              }
            />
          </div>
        </div>
        <div className="flex flex-col gap-[4px] text-sm">
          <div className="text-sm">KPI Equation</div>
          <div className="flex items-center gap-2 text-sm self-start">
            {astTokens.map((value, i) => (
              <div
                key={i}
                className="flex items-center rounded bg-v2-action-tertiary px-3.5 py-0.5"
              >
                {value}
              </div>
            ))}
          </div>
        </div>
      </div>
      <ExportChannelInsightsModal
        columns={columns}
        selectedKpi={selectedKpi}
        costCurvesRawData={costCurvesRawData}
        isOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
      />
    </div>
  );
};
